import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import Modal, { ModalProps } from '../Modal';
import Button from '../Button';

export type AutomationReference = {
  automationRuleId: string;
  name: string;
  location: 'action' | 'trigger';
};

export type ResourceInUseModalProps = {
  resourceType: 'tag' | 'email_template';
  resourceName: string;
  automationReferences: AutomationReference[];
  canViewAutomations?: boolean;
  providerName?: string;
} & Omit<ModalProps, 'children' | 'name' | 'title'>;

const ResourceInUseModal: FC<ResourceInUseModalProps> = ({
  resourceType,
  resourceName,
  automationReferences,
  canViewAutomations = true,
  providerName,
  isOpen,
  setClosed,
}) => {
  const navigate = useNavigate();
  const resourceLabel = resourceType === 'tag' ? 'tag' : 'Mail template';
  const automationCount = automationReferences.length;
  const automationLabel = automationCount === 1 ? 'automation' : 'automations';

  const handleAutomationClick = (automationRuleId: string) => {
    // Navigate to automations page with state to open the specific automation editor
    navigate('/automations/rules', {
      state: { automationRuleId },
    });
    setClosed();
  };

  return (
    <Modal
      name="ResourceInUse"
      width="small"
      isOpen={isOpen}
      setClosed={setClosed}
    >
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 text-center">
        <div className="bg-amber-50 mt-3 flex h-12 w-12 items-center justify-center rounded-full">
          <ExclamationCircleIcon
            className="text-amber-100 h-6 w-6"
            aria-hidden
          />
        </div>

        <h1 className="mt-4 font-serif text-subtitle-small text-green-150">
          {resourceLabel === 'tag'
            ? 'Tag in use by automations'
            : 'Mail template in use by automations'}
        </h1>

        <div className="mt-3 w-full text-left font-sans text-body text-green-125">
          <p className="mb-4">
            The <strong>"{resourceName}"</strong> {resourceLabel} cannot be
            deleted because it is currently in use by {automationCount}{' '}
            {automationLabel}:
          </p>

          <ul className="mb-4 list-disc space-y-2 pl-5">
            {automationReferences.map((ref, index) => (
              <li key={`${ref.name}-${index}`}>
                {canViewAutomations && ref.automationRuleId ? (
                  <button
                    type="button"
                    className="hover:text-blue-75 cursor-pointer border-0 bg-transparent p-0 font-sans text-body text-blue-100 underline"
                    onClick={() => handleAutomationClick(ref.automationRuleId)}
                  >
                    {ref.name}
                  </button>
                ) : (
                  <span>"{ref.name}"</span>
                )}
                {ref.location === 'trigger' && (
                  <span className="ml-2 text-small-caption text-neutral-110">
                    (event filter)
                  </span>
                )}
              </li>
            ))}
          </ul>

          <p className="text-sm text-neutral-110">
            {canViewAutomations
              ? `Please remove the ${resourceLabel} from the automation(s) above before deleting.`
              : `Please contact your organization admin to modify these automations.${
                  providerName ? ` (${providerName})` : ''
                }`}
          </p>
        </div>

        <div className="mb-5 mt-6 flex w-full flex-row items-center justify-center">
          <Button
            title="Got it"
            theme="secondary"
            size="medium"
            onClick={setClosed}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ResourceInUseModal;
